/**
 * What a hangar screen shows, chosen by the owner (Kyle, 2026-10-03: "every feature that the weather
 * screen currently has should be modular and selectable by the dzo whether or not they want it on their
 * display").
 *
 * One list, shared by the API (which validates what an owner saves), the owner screens (which draw the
 * switches) and the hangar screen (which reads the result). A site stores only the keys the owner has
 * set (`sites.screen_features`, a JSON object of key → true/false); every other key takes its default, so
 * a site that has never touched the setting shows the screen as it was before the setting existed, and a
 * screen reading an older server (no field at all) shows the same.
 *
 * Two parts of the screen are not here on purpose: the wind reading itself, and the line that says the
 * screen has fallen back to another source or that nothing is reporting. The first is the screen; the
 * second is a fact about the data, which the owner must not be able to hide.
 */

export const SCREEN_FEATURE_GROUPS = ['Header', 'Wind', 'Windsock', 'Field report', 'Forecast', 'Footer'] as const
export type ScreenFeatureGroup = (typeof SCREEN_FEATURE_GROUPS)[number]

export interface ScreenFeature {
  key: string
  /** The owner's words for it. */
  name: string
  group: ScreenFeatureGroup
  /** Shown when the owner has not said otherwise. */
  default: boolean
}

export const SCREEN_FEATURES = [
  { key: 'clock', name: 'Clock (local and Zulu)', group: 'Header', default: true },
  { key: 'limitDots', name: 'Your limits, as dots above the wind direction', group: 'Wind', default: true },
  { key: 'gustLullSpread', name: 'Gust, lull and spread', group: 'Wind', default: true },
  { key: 'facts', name: 'Ceiling, visibility, temperature, pressure', group: 'Wind', default: true },
  { key: 'windChart', name: 'Wind chart (last 2 hours)', group: 'Wind', default: true },
  { key: 'nextReport', name: 'When the next report is due', group: 'Wind', default: true },
  { key: 'windsock', name: 'Windsock and wind direction', group: 'Windsock', default: true },
  { key: 'recentGusts', name: 'Recent peak gusts', group: 'Windsock', default: true },
  { key: 'fieldReport', name: 'Latest field report, raw and in plain words', group: 'Field report', default: true },
  { key: 'flightCategory', name: 'Flight category (VFR, MVFR, IFR, LIFR)', group: 'Field report', default: true },
  { key: 'remarks', name: 'Remarks', group: 'Field report', default: true },
  { key: 'windsAloft', name: 'Winds aloft', group: 'Forecast', default: false },
  { key: 'forecast', name: 'Hourly forecast', group: 'Forecast', default: false },
  { key: 'daylight', name: 'Sunset and daylight remaining', group: 'Footer', default: true },
] as const satisfies readonly ScreenFeature[]

export type ScreenFeatureKey = (typeof SCREEN_FEATURES)[number]['key']

/** What a site stores: only the keys the owner has set. */
export type StoredScreenFeatures = Partial<Record<ScreenFeatureKey, boolean>>

/** Every key, decided: the stored value where there is one, else the default. */
export type ScreenFeatureSet = Record<ScreenFeatureKey, boolean>

export const SCREEN_FEATURE_KEYS: readonly ScreenFeatureKey[] = SCREEN_FEATURES.map((f) => f.key)

export function isScreenFeatureKey(key: string): key is ScreenFeatureKey {
  return (SCREEN_FEATURE_KEYS as readonly string[]).includes(key)
}

/**
 * Decide every feature from what a site stores. Anything that is not an object, any unknown key and any
 * non-boolean value is ignored rather than refused: the screen must draw whatever the server sends,
 * including nothing at all (an older server) and a key a newer server knows and this build does not.
 */
export function resolveScreenFeatures(stored: unknown): ScreenFeatureSet {
  const out = {} as ScreenFeatureSet
  const given = typeof stored === 'object' && stored !== null && !Array.isArray(stored) ? (stored as Record<string, unknown>) : {}
  for (const f of SCREEN_FEATURES) {
    const v = given[f.key]
    out[f.key] = typeof v === 'boolean' ? v : f.default
  }
  return out
}

export type ScreenFeaturesCheck = { ok: true; features: StoredScreenFeatures } | { ok: false; problem: string }

/**
 * What an owner may save: an object whose keys are known features and whose values are true or false.
 * Refused, not ignored, with the reason: a misspelt key would otherwise be saved and change nothing.
 */
export function checkScreenFeatures(value: unknown): ScreenFeaturesCheck {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { ok: false, problem: '"screenFeatures" must be an object of feature → true/false, such as {"windsock": false}.' }
  }
  const out: StoredScreenFeatures = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (!isScreenFeatureKey(k)) return { ok: false, problem: `"${k}" is not a hangar-screen feature (the features are ${SCREEN_FEATURE_KEYS.join(', ')}).` }
    if (typeof v !== 'boolean') return { ok: false, problem: `"screenFeatures.${k}" must be true or false.` }
    out[k] = v
  }
  return { ok: true, features: out }
}

/** The stored map after an owner's change: the change's keys win, every other stored key is kept. */
export function mergeScreenFeatures(stored: unknown, change: StoredScreenFeatures): StoredScreenFeatures {
  const base = typeof stored === 'object' && stored !== null && !Array.isArray(stored) ? (stored as Record<string, unknown>) : {}
  const out: StoredScreenFeatures = {}
  for (const [k, v] of Object.entries(base)) if (isScreenFeatureKey(k) && typeof v === 'boolean') out[k] = v
  return { ...out, ...change }
}
